/** `x-widget: "integration-preset"` — picks the versioned ingestion preset for
 *  this restech, and shows what it actually does.
 *
 *  The viewer is the point. A preset ref is an opaque string, and an operator
 *  choosing one is choosing which tags start being written — so the rules, the
 *  evidence behind each ("21/22 tenants"), and any natively-emitted categories
 *  have to be legible before the choice, not discoverable afterwards by
 *  inspecting bookings.
 *
 *  Read-only by construction: presets live in the backend's code and a shipped
 *  one is immutable. Per-rule changes are `preset_overrides`, a separate key.
 */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as presetsApi from '@/api/integrationPresets';
import type { IntegrationPreset, PresetRule } from '@/api/integrationPresets';

interface Props {
  restech: string | null;
  value: unknown;
  onChange: (next: string | null) => void;
  readOnly?: boolean;
}

const NONE = '__none__';

function RuleRow({ rule }: { rule: PresetRule }) {
  const target = rule.tag_category ?? (rule.tag_context ? `context.${rule.tag_context}` : '—');
  return (
    <Box sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {target}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ←
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {rule.path ?? (rule.sources ?? []).join('  ›  ')}
        </Typography>
        {rule.sources && (
          <Tooltip title="Ordered alternatives — the first that matches wins. Not a union.">
            <Chip size="small" variant="outlined" label="first match" />
          </Tooltip>
        )}
        {rule.convergence === 'set' && (
          <Tooltip title="Repeated values collapse to one tag each.">
            <Chip size="small" variant="outlined" label="unique" />
          </Tooltip>
        )}
        {rule.transform && <Chip size="small" variant="outlined" label={rule.transform} />}
        {rule.when && <Chip size="small" variant="outlined" label={`when: ${rule.when}`} />}
      </Stack>
      {rule.attributes && rule.attributes.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ pl: 1 }}>
          attributes:{' '}
          {rule.attributes
            .map((a) => `${a.name}${a.lang ? ' (per language)' : ''} ← ${a.path}`)
            .join(', ')}
        </Typography>
      )}
      {rule.notes && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ pl: 1 }}>
          {rule.notes}
        </Typography>
      )}
    </Box>
  );
}

export function IntegrationPresetWidget({ restech, value, onChange, readOnly }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['integration_presets', restech],
    queryFn: () => presetsApi.list(restech ?? undefined),
    enabled: Boolean(restech),
    staleTime: 5 * 60_000,
  });

  const presets: IntegrationPreset[] = data?.results ?? [];
  const current = typeof value === 'string' && value ? value : null;
  const selected = presets.find((p) => p.ref === current) ?? null;

  if (!restech) return null;

  return (
    <Stack spacing={1} sx={{ width: '100%', py: 0.5 }}>
      <TextField
        select
        size="small"
        label="Preset"
        value={current ?? NONE}
        disabled={readOnly || isLoading}
        sx={{ maxWidth: 360 }}
        onChange={(e) => onChange(e.target.value === NONE ? null : e.target.value)}
        helperText={
          selected
            ? selected.summary
            : isLoading
              ? 'Loading presets…'
              : presets.length === 0
                ? 'No preset is published for this integration yet.'
                : 'No preset — this tenant’s own tag mapping rules are the only ones that run.'
        }
      >
        <MenuItem value={NONE}>None</MenuItem>
        {/* The stored ref always has an option, even while the list is still
            loading or when it names a preset that was never published —
            otherwise the Select is briefly out of range and shows nothing,
            which reads as "no preset" for the exact config that has one. */}
        {current && !selected && <MenuItem value={current}>{current}</MenuItem>}
        {presets.map((p) => (
          <MenuItem key={p.ref} value={p.ref}>
            {p.ref}
          </MenuItem>
        ))}
      </TextField>

      {/* A ref the registry does not know: the tenant keeps ingesting on its own
          rules, so say that rather than showing an empty preset. */}
      {current && !selected && !isLoading && (
        <Typography variant="caption" color="warning.main">
          `{current}` is not a published preset. Ingestion is running on this tenant’s own tag
          mapping alone.
        </Typography>
      )}

      {selected && (
        <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">
              What it writes — {selected.rules.length} rule
              {selected.rules.length === 1 ? '' : 's'}
              {selected.native_categories.length > 0 &&
                `, plus ${selected.native_categories.length} written natively`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {selected.native_categories.length > 0 && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ pb: 1 }}>
                Emitted by the integration itself, with no rule:{' '}
                {selected.native_categories.join(', ')}. A rule for one of these would write the tag
                twice.
              </Typography>
            )}
            {selected.rules.map((rule) => (
              <RuleRow key={rule.id} rule={rule} />
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  );
}
